import { useCallback } from 'preact/hooks';
import { route } from 'preact-router';
import { useDispatch } from 'react-redux';

import { useSelector, useStore } from '@/redux/helper';
import persistor from '@/redux/persistor';
import { completeTour, goBack, goToStep, restartTour, skipTour, startTour } from '@/redux/slices/tours/actions';
import { selectCurrentStepId, selectIsTourRunning, selectTour } from '@/redux/slices/tours/selectors';

import trackTour, { stepPayload } from '../analytics';
import { useTourContext } from '../context';
import { backNavigationPath, goBackDistance } from '../core/backTarget';
import { resolveButtonPath } from '../core/resolvePath';
import { resolveUi } from '../core/resolveUi';
import { getStep, getTour } from '../registry';
import { BannerButton, OnAppEventConfig } from '../types';

interface ControllerOptions {
  isDev: boolean;
}

/**
 * Drives one tour. Deliberately thin: there is no pause/resume/auto-pause machinery — a step's
 * ui config decides what renders (via `when`) and its buttons carry every transition. The only
 * mechanism here is `goBack`, which pops the history stack because merge points make a
 * pre-authored back target impossible.
 */
const useTourController = (tourId: string, { isDev }: ControllerOptions) => {
  const dispatch = useDispatch();
  const store = useStore();

  // Subscribe to primitives only. `selectTour` merges client progress with server analytics into
  // a NEW object on every call, so subscribing to it re-renders this tree on every dispatch in
  // the app (chat, stream, …) and churns every callback identity with it. The merged state is
  // only ever read at the moment a callback fires, so read it lazily from the store instead —
  // which is also fresher than a render-time snapshot.
  const currentStepId = useSelector(selectCurrentStepId(tourId));
  const isActive = useSelector(selectIsTourRunning(tourId));

  const registration = getTour(tourId);
  const currentStep = currentStepId ? getStep(tourId, currentStepId) : undefined;

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  // The page-supplied context. Unlike the store, this is SUBSCRIBED to rather than read lazily:
  // a step's copy and its variant choice both depend on it, so a change has to re-render.
  const context = useTourContext();
  // Resolve every component's path- and condition-varied config once, here; consumers use `ui`.
  const ui = resolveUi(currentStep, pathname, context);

  const track = useCallback((action: string, extra?: Record<string, unknown>) => trackTour(tourId, action, extra), [tourId]);

  /**
   * The single advance path. Every mechanism (buttons, auto-advance, media-close, modals) goes
   * through here, so 'step-entered' is emitted at the moment we move — not by an effect that
   * observes the step has already changed.
   */
  const advance = useCallback(
    (stepId: string) => {
      const step = getStep(tourId, stepId);
      track('step-entered', stepPayload(tourId, stepId, selectTour(tourId)(store.getState())));
      // stepNumber travels with the action so the slice can keep the high-water mark
      // monotonic without importing the tour config.
      dispatch(goToStep({ tourId, stepId, stepNumber: step?.analytics?.stepNumber }));
      // Terminal steps carry no UI, so clearing the tour here takes nothing off the screen.
      if (step?.completesTour) dispatch(completeTour({ tourId }));
    },
    [dispatch, store, tourId, track],
  );

  /**
   * A user action the current step was waiting on. Beyond advancing, a listener may retire other
   * tours: the action can be meaningful to more than the tour that happened to be on screen —
   * taking a clip completes the habitat onboarding in every scenario, not just this one.
   *
   * Ordering matters. Completing runs first so the sibling tours are already cleared when the
   * arbiter re-runs on the advance; doing it after would let a sibling win the screen for a
   * render. `completeTour` is idempotent, so re-firing is harmless.
   */
  const handleAppEvent = useCallback(
    (config: OnAppEventConfig) => {
      config.completeTours?.forEach((id) => {
        if (id === tourId) return; // clearing ourselves here would erase the step mid-advance
        dispatch(completeTour({ tourId: id }));
      });
      advance(config.targetStepId);
    },
    [dispatch, tourId, advance],
  );

  const goBackStep = useCallback(
    (alreadyNavigated = false) => {
      const state = store.getState();
      const history = state.tours.tours[tourId]?.stepHistory ?? [];
      // May be more than one: steps that opened a media modal cannot be landed on again.
      const steps = goBackDistance(tourId, history);
      if (steps === 0) return;

      const previousId = history[history.length - 1 - steps];
      const previousStep = getStep(tourId, previousId);

      // Back is the only transition without an authored path, so it has to find its own: the
      // step we are returning to may live on another page, and its banner would fall through to
      // the paused catch-all if we stayed here. A path on the button itself still wins.
      if (!alreadyNavigated) {
        const backPath = backNavigationPath(previousStep, window.location.pathname, isDev);
        if (backPath) route(backPath);
      }

      track('step-entered', stepPayload(tourId, previousId, selectTour(tourId)(state)));
      dispatch(goBack({ tourId, steps }));
    },
    [dispatch, store, tourId, track, isDev],
  );

  const handleButtonClick = useCallback(
    (button: BannerButton) => {
      const state = store.getState();
      const stepId = selectCurrentStepId(tourId)(state) ?? '';
      const path = resolveButtonPath(button, isDev, state.user.username ?? undefined);

      if (button.restartTour) {
        // May restart a different tour (e.g. the post-tour banner restarts the journey).
        const targetTourId = button.restartTourId ?? tourId;
        const target = getTour(targetTourId);
        // Reported against the tour being restarted, so tourName identifies which one. Keeps the
        // button's own action label when it has one (e.g. 'switched-habitat').
        trackTour(targetTourId, button.trackAction ?? 'restarted', { stepId, ...(path && { navigatesTo: path }) });
        dispatch(restartTour({ tourId: targetTourId, firstStepId: target?.firstStepId ?? stepId }));
        // A restart may name where to go next (switching habitat restarts the onboarding on the
        // destination); a restart with no path falls back home, as the guided journey's does.
        route(path ?? '/home');
        return;
      }

      // A button may relabel its event (e.g. 'resumed', 'went-back', 'skipped-ahead').
      track(button.trackAction ?? 'button-clicked', {
        ...stepPayload(tourId, stepId, selectTour(tourId)(state)),
        buttonLabel: button.label,
        ...(path && { navigatesTo: path }),
      });

      if (path && button.forceReload) {
        if (button.targetStepId) advance(button.targetStepId);
        // Flush persisted state before hard navigation so tour state survives the reload.
        persistor.flush().finally(() => {
          window.location.href = path;
        });
        return;
      }

      if (path) route(path);

      if (button.completeTour) dispatch(completeTour({ tourId }));

      // A button either pops the stack or names its target — never both.
      if (button.goBack) {
        goBackStep(Boolean(path));
      } else if (button.targetStepId) {
        advance(button.targetStepId);
      }
    },
    [dispatch, store, isDev, tourId, track, advance, goBackStep],
  );

  return {
    isActive,
    currentStep,
    currentStepId,
    ui,
    handleButtonClick,
    handleAppEvent,
    advance,
    skip: () => dispatch(skipTour({ tourId })),
    complete: () => dispatch(completeTour({ tourId })),
    start: () => dispatch(startTour({ tourId, firstStepId: registration?.firstStepId ?? '' })),
  };
};

export default useTourController;
